import { create } from 'zustand'
import { persist, createJSONStorage } from 'zustand/middleware'

const useAuthStore = create(
  persist(
    (set, get) => ({
      user: null,
      isAuthenticated: false,
      isLoading: false,
      error: null,

      login: async (credentials) => {
        set({ isLoading: true, error: null })
        try {
          const response = await fetch('/api/auth/login', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify(credentials),
          })

          if (!response.ok) {
            throw new Error('Login failed')
          }

          const user = await response.json()
          set({
            user,
            isAuthenticated: true,
            isLoading: false,
            error: null
          })

          return user
        } catch (error) {
          set({
            error: error.message,
            isLoading: false,
            user: null,
            isAuthenticated: false
          })
          throw error
        }
      },

      googleLogin: async (googleToken) => {
        set({ isLoading: true, error: null })
        try {
          const response = await fetch('/api/auth/google', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ token: googleToken }),
          })

          if (!response.ok) {
            throw new Error('Google login failed')
          }

          const user = await response.json()
          set({
            user,
            isAuthenticated: true,
            isLoading: false,
            error: null
          })

          return user
        } catch (error) {
          set({
            error: error.message,
            isLoading: false,
            user: null,
            isAuthenticated: false
          })
          throw error
        }
      },

      register: async (userData) => {
        set({ isLoading: true, error: null })
        try {
          const response = await fetch('/api/auth/register', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify(userData),
          })

          if (!response.ok) {
            throw new Error('Registration failed')
          }

          const user = await response.json()
          set({
            user,
            isAuthenticated: true,
            isLoading: false,
            error: null
          })

          return user
        } catch (error) {
          set({
            error: error.message,
            isLoading: false,
            user: null,
            isAuthenticated: false
          })
          throw error
        }
      },

      logout: () => {
        set({
          user: null,
          isAuthenticated: false,
          error: null
        })
        // Clear any auth tokens
        localStorage.removeItem('auth-storage')
      },

      updateProfile: async (updates) => {
        const { user } = get()
        if (!user) return

        set({ isLoading: true, error: null })
        try {
          const response = await fetch(`/api/users/${user.id}`, {
            method: 'PATCH',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify(updates),
          })

          if (!response.ok) {
            throw new Error('Profile update failed')
          }

          const updatedUser = await response.json()
          set({
            user: updatedUser,
            isLoading: false,
            error: null
          })

          return updatedUser
        } catch (error) {
          set({
            error: error.message,
            isLoading: false
          })
          throw error
        }
      },

      clearError: () => set({ error: null }),
    }),
    {
      name: 'auth-storage',
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        user: state.user,
        isAuthenticated: state.isAuthenticated
      }),
    }
  )
)

export default useAuthStore